import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useComparableField } from '../../context/useComparableField'
import {
  type SubTemplateFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { DocumentationEntry } from '../documentationEntry/DocumentationEntry'
import { TemplateDocumentationEntry } from '../documentationEntry/TemplateDocumentationEntry'
import { EditableMarkdownViewer } from '../markdownEditor/EditableMarkdownViewer'
import { ConfigurationMarkdownViewer } from '../markdownviewer/ConfigurationMarkdownViewer'
import { parentFromSubTemplateFieldValue } from '../subTemplateParentSelector/helpers'
import { SubTemplateParentSelector } from '../subTemplateParentSelector/SubTemplateParentSelector'
import { SubtemplateVariantDocumentationEditor } from '../templateDocumentationEditors/SubtemplateVariantDocumentationEditor'
import { subTemplatedFieldValueDocumentation } from './helpers'
import { IdentifiableSubTemplateIdEditor } from './IdentifiableSubTemplateIdEditor'
import { InheritedValueDocumentationEntry } from './InheritedValueDocumentationEntry'
import { useFieldDocumentation } from './useFieldDocumentation'

type Props = {
  variant: SubTemplateFieldValue
  isExtendedView: boolean
  areFieldsShownAsEditable: boolean
}

export const SubTemplatedFieldValueDocumentation = ({
  variant,
  isExtendedView,
  areFieldsShownAsEditable,
}: Props) => {
  assertNotEmpty(variant.subTemplatedField)

  const { modelFieldTypeDocumentation, modelFieldDocumentation, values } =
    useFieldDocumentation(variant.subTemplatedField)
  const {
    configurationFieldDocumentation,
    configurationFieldDocumentationPath,
  } = useMemo(() => {
    const documentation = subTemplatedFieldValueDocumentation(variant)
    assertNotEmpty(documentation)

    return documentation
  }, [variant])

  const { isDraftTemplate } = useTemplateContext()
  const translate = useTranslate()

  const comparableField = useComparableField<SubTemplateFieldValue>(
    variant.path,
  )

  const documentationValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<SubTemplateFieldValue>) =>
      comparable?.documentation
        ? stringifyComparableValue(comparable.documentation)
        : '',
    [],
  )

  const parentValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<SubTemplateFieldValue>) => {
      if (!comparable) {
        return ''
      }

      const parent = parentFromSubTemplateFieldValue(comparable)

      return stringifyComparableValue(parent)
    },
    [],
  )

  return (
    <>
      <TemplateDocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.configurationFieldDocumentation',
        )}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={documentationValueFromComparable}
            compareFilter="variantDocumentation"
          />
        }
        value={
          areFieldsShownAsEditable ? (
            <SubtemplateVariantDocumentationEditor
              variant={variant}
              forField={variant.subTemplatedField}
            />
          ) : (
            <EditableMarkdownViewer
              value={configurationFieldDocumentation?.value}
            />
          )
        }
        origin={variant.documentation?.origin}
        path={configurationFieldDocumentationPath}
      />
      {variant.subTemplatedField.role ===
      ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE ? (
        <DocumentationEntry
          title={translate('templateViewer.fieldDetails.field.subTemplateId')}
          value={
            isDraftTemplate && !isExtendedView ? (
              <IdentifiableSubTemplateIdEditor variant={variant} />
            ) : (
              variant.subTemplateIdentifier
            )
          }
        />
      ) : null}
      <DocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.modelFieldDocumentation',
        )}
        value={
          <div className="flex flex-col gap-y-4">
            <ConfigurationMarkdownViewer shouldTransformLinksToDocSite>
              {modelFieldDocumentation || '\\-'}
            </ConfigurationMarkdownViewer>
          </div>
        }
      />
      <DocumentationEntry
        title={translate(
          'templateViewer.fieldDetails.field.modelFieldTypeDocumentation',
        )}
        value={
          <div className="flex flex-col gap-y-4">
            <ConfigurationMarkdownViewer shouldTransformLinksToDocSite>
              {modelFieldTypeDocumentation || '\\-'}
            </ConfigurationMarkdownViewer>
            {values && (
              <div className="text-sm italic text-gray-300">
                {JSON.stringify(values, null, 2).replaceAll('"', '')}
              </div>
            )}
          </div>
        }
      />
      <TemplateDocumentationEntry
        title={translate('templateViewer.fieldDetails.field.variant.parentId')}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={parentValueFromComparable}
            compareFilter="parentId"
          />
        }
        value={
          isDraftTemplate && !isExtendedView ? (
            <SubTemplateParentSelector variant={variant} />
          ) : (
            (variant.parent?.value ?? '-')
          )
        }
        origin={variant.parent?.origin}
        path={`${variant.path}.parent`}
      />
      <InheritedValueDocumentationEntry value={variant} />
    </>
  )
}
